import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VCardContact } from '../api/types';
import { FieldLabel } from './FieldLabel';

export interface VCardFormProps {
  /**
   * Controlled from the page, unlike `QrCodeForm` which owns its own state: the directory pre-fill has to be
   * able to replace what is in the fields after the form is already on screen.
   */
  values: VCardContact;
  onChange: (values: VCardContact) => void;
  submitting?: boolean;
  /** Without one the form has no submit button: the page acts on the values itself, as the vCard page does. */
  onSubmit?: () => void | Promise<void>;
  /** Folds the address and website into a section of their own, closed until asked for. */
  collapseExtras?: boolean;
  /** Offered when a directory profile is available to pull again after edits. */
  onRefill?: () => void;
  refillLabel?: string;
  submitLabel?: string;
  submittingLabel?: string;
  submitIcon?: string;
  onCancel?: () => void;
  /** Offers an eye on each field to keep it but leave it off the card. */
  hideable?: boolean;
  /** Offers the choice of carrying the Entra photo. */
  photoChoice?: boolean;
  /** What Entra holds for the fields it differs on, each offered to take with one click. */
  entraValues?: Partial<Record<keyof VCardContact, string>>;
}

type TextField = Exclude<keyof VCardContact, 'hiddenFields' | 'includePhoto'>;

const EXTRAS: ReadonlySet<TextField> = new Set([
  'website',
  'linkedin',
  'street',
  'city',
  'stateRegion',
  'postcode',
  'country',
  'note',
]);

/** Every field, in the order they read on a business card. Only the name has to be filled in. */
const FIELDS: ReadonlyArray<{
  key: TextField;
  icon: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}> = [
  { key: 'firstName', icon: 'pi-user', required: true },
  { key: 'lastName', icon: 'pi-user', required: true },
  { key: 'title', icon: 'pi-briefcase', placeholder: 'Regional sales manager' },
  { key: 'organization', icon: 'pi-building', placeholder: 'Kersia' },
  { key: 'email', icon: 'pi-envelope', type: 'email', placeholder: 'name@kersia-group.com' },
  { key: 'mobile', icon: 'pi-mobile', type: 'tel', placeholder: '+33 6 12 34 56 78' },
  { key: 'workPhone', icon: 'pi-phone', type: 'tel' },
  { key: 'website', icon: 'pi-globe', type: 'url', placeholder: 'https://www.kersia-group.com' },
  { key: 'linkedin', icon: 'pi-linkedin', type: 'url', placeholder: 'https://www.linkedin.com/in/...' },
  { key: 'street', icon: 'pi-map-marker' },
  { key: 'city', icon: 'pi-map-marker' },
  { key: 'stateRegion', icon: 'pi-map' },
  { key: 'postcode', icon: 'pi-map' },
  { key: 'country', icon: 'pi-flag' },
  { key: 'note', icon: 'pi-comment', type: 'textarea' },
];

/** What stops a card from being saved, or null. Shared with the pages that act on the values themselves. */
export function contactValidationError(values: VCardContact, t: (key: string) => string): string | null {
  if (!values.firstName?.trim() || !values.lastName?.trim()) {
    return t('vcard.validation.nameRequired');
  }

  const email = values.email?.trim() ?? '';
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return t('vcard.validation.email');
  }

  const website = values.website?.trim() ?? '';
  if (website !== '' && !isHttpUrl(website)) {
    return t('vcard.validation.website');
  }

  const linkedin = values.linkedin?.trim() ?? '';
  if (linkedin !== '' && !isHttpUrl(linkedin)) {
    return t('vcard.validation.linkedin');
  }

  return null;
}

function isHttpUrl(candidate: string): boolean {
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Only the name is required; the rest is emitted only when filled in, and anything pre-filled stays editable. */
export function VCardForm({
  values,
  onChange,
  submitting = false,
  onSubmit,
  collapseExtras = false,
  onRefill,
  refillLabel,
  submitLabel,
  submittingLabel,
  submitIcon = 'pi-qrcode',
  onCancel,
  hideable = false,
  photoChoice = false,
  entraValues = {},
}: VCardFormProps) {
  const { t } = useTranslation();
  const [validationError, setValidationError] = useState<string | null>(null);
  const hidden = values.hiddenFields ?? [];

  function set(key: TextField, value: string) {
    onChange({ ...values, [key]: value });
  }

  function toggleHidden(key: TextField) {
    onChange({
      ...values,
      hiddenFields: hidden.includes(key) ? hidden.filter((field) => field !== key) : [...hidden, key],
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!onSubmit) {
      return;
    }

    const problem = contactValidationError(values, t);
    setValidationError(problem);
    if (problem === null) {
      await onSubmit();
    }
  }

  function renderField(field: (typeof FIELDS)[number]) {
    const id = `vcard-${field.key}`;
    const label = t(`vcard.fields.${field.key}`);
    const isHidden = hidden.includes(field.key);
    const entra = entraValues[field.key];
    const common = {
      id,
      className: `form-control${isHidden ? ' is-off-card' : ''}`,
      'aria-required': field.required,
      value: values[field.key] ?? '',
      placeholder: field.placeholder,
      disabled: submitting,
    };

    return (
      <div className="field" key={field.key}>
        <div className="vcard-field__label">
          <FieldLabel htmlFor={id} icon={field.icon} required={field.required}>
            {label}
          </FieldLabel>
          {hideable && !field.required && (
            <button
              type="button"
              className="btn btn-link btn-sm vcard-field__visibility"
              aria-pressed={isHidden}
              aria-label={t(isHidden ? 'vcard.showOnCard' : 'vcard.hideFromCard', { field: label })}
              title={t(isHidden ? 'vcard.showOnCard' : 'vcard.hideFromCard', { field: label })}
              disabled={submitting}
              onClick={() => toggleHidden(field.key)}
            >
              <i className={`pi ${isHidden ? 'pi-eye-slash' : 'pi-eye'}`} aria-hidden="true" />
            </button>
          )}
        </div>
        {field.type === 'textarea' ? (
          <textarea {...common} rows={2} onChange={(event) => set(field.key, event.target.value)} />
        ) : (
          <input {...common} type={field.type ?? 'text'} onChange={(event) => set(field.key, event.target.value)} />
        )}
        {isHidden && <span className="hint">{t('vcard.offCard')}</span>}
        {entra !== undefined && (
          <span className="vcard-field__entra">
            {t('vcard.entraHas', { value: entra })}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              disabled={submitting}
              onClick={() => set(field.key, entra)}
            >
              {t('vcard.useEntra')}
            </button>
          </span>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-label={t('vcard.formLabel')}>
      {validationError && (
        <div className="alert alert-danger d-flex gap-2" role="alert">
          <i className="pi pi-exclamation-circle mt-1" aria-hidden="true" />
          <span>{validationError}</span>
        </div>
      )}

      {photoChoice && (
        <div className="form-check form-switch mb-3">
          <input
            id="vcard-include-photo"
            type="checkbox"
            role="switch"
            className="form-check-input"
            checked={values.includePhoto !== false}
            disabled={submitting}
            onChange={(event) => onChange({ ...values, includePhoto: event.target.checked })}
          />
          <label className="form-check-label" htmlFor="vcard-include-photo">
            {t('vcard.includePhoto')}
          </label>
        </div>
      )}

      {collapseExtras ? (
        <>
          <div className="vcard-fields">{FIELDS.filter((field) => !EXTRAS.has(field.key)).map(renderField)}</div>
          <details className="vcard-extras">
            <summary>{t('vcard.addressAndWebsite')}</summary>
            <div className="vcard-fields">{FIELDS.filter((field) => EXTRAS.has(field.key)).map(renderField)}</div>
          </details>
        </>
      ) : (
        <div className="vcard-fields">{FIELDS.map(renderField)}</div>
      )}

      <p className="hint">{t('vcard.optionalHint')}</p>

      {(onSubmit || onRefill || onCancel) && (
        <div className="form-actions">
          {onSubmit && (
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              <i className={`pi ${submitting ? 'pi-spin pi-spinner' : submitIcon} me-2`} aria-hidden="true" />
              {submitting ? (submittingLabel ?? t('vcard.generating')) : (submitLabel ?? t('vcard.generate'))}
            </button>
          )}
          {onCancel && (
            <button type="button" className="btn btn-light" onClick={onCancel} disabled={submitting}>
              {t('qrCode.contact.cancel')}
            </button>
          )}
          {onRefill && (
            <button type="button" className="btn btn-light" onClick={onRefill} disabled={submitting}>
              <i className="pi pi-refresh me-2" aria-hidden="true" />
              {refillLabel ?? t('vcard.resetFromDirectory')}
            </button>
          )}
        </div>
      )}
    </form>
  );
}
